import React from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  width?: number;
  height?: number;
  priority?: boolean;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
}

/**
 * Simplified optimized image component that directly loads images
 * without complex lazy loading to ensure reliability
 */
export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  sizes = '100vw',
  loading = 'lazy',
  width,
  height,
  priority = false,
  objectFit = 'cover'
}) => {
  // Generate clean paths
  const cleanSrc = src.startsWith('/') ? src : `/${src}`;

  return (
    <img
      src={cleanSrc}
      alt={alt}
      className={className}
      loading={priority ? 'eager' : loading}
      width={width}
      height={height}
      decoding={priority ? 'sync' : 'async'}
      style={{ objectFit }}
      sizes={sizes}
      // Add fetch priority for LCP images
      {...(priority && { fetchPriority: 'high' } as any)}
    />
  );
};

/**
 * Responsive image component with automatic sizing based on container
 */
export const ResponsiveImage: React.FC<Omit<OptimizedImageProps, 'sizes'>> = (props) => {
  // Responsive sizes based on typical layout breakpoints
  const responsiveSizes = [
    '(max-width: 640px) 100vw',   // Mobile: full width
    '(max-width: 768px) 100vw',   // Tablet: full width
    '(max-width: 1024px) 50vw',   // Small desktop: half width
    '(max-width: 1280px) 33vw',   // Desktop: third width
    '25vw'                         // Large desktop: quarter width
  ].join(', ');

  return <OptimizedImage {...props} sizes={responsiveSizes} />;
};

/**
 * Background image component with lazy loading
 */
export const OptimizedBackgroundImage: React.FC<{
  src: string;
  className?: string;
  children?: React.ReactNode;
  priority?: boolean;
}> = ({ src, className = '', children }) => {
  const cleanSrc = src.startsWith('/') ? src : `/${src}`;

  return (
    <div
      className={className}
      style={{
        backgroundImage: `url(${cleanSrc})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {children}
    </div>
  );
};

/**
 * Logo component with specific optimizations
 */
export const OptimizedLogo: React.FC<{ className?: string }> = ({ className }) => {
  // For now, use the original logo directly until optimized versions are generated
  return (
    <img
      src="/logo-favicon.png"
      alt="Ticket Hauk Logo"
      className={className}
      width={80}
      height={80}
      loading="eager"
      decoding="sync"
      fetchPriority="high"
    />
  );
};

export default OptimizedImage;